import React, { useState, useEffect } from "react";
import { View, ScrollView, StyleSheet, TouchableOpacity } from "react-native";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import ThemedInput from "@/components/ThemedInput";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import useToast from "@/hooks/useToast";
import { getEntityPermissions } from "@/lib/permissions";
import { roleService } from "@/services/roleService";
import { supabase } from "@/lib/supabase-client";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { UserPicker } from "@/components/Pickers/UserPicker";
import { PhonePicker } from "@/components/Pickers/PhonePicker";

// Role data interface matching the edit page
interface RoleFormData {
  name: string;
  description?: string;
  meta_data?: {
    users?: string[];
    permissions?: any;
    accept_shifts?: boolean;
    priority?: number;
  };
}

// Permission categories for basic setup
const BOOLEAN_PERMISSIONS = [
  { key: "admin_portal", label: "Admin Portal", description: "Access to admin portal and advanced features" },
];

const CRUD_PERMISSION_CATEGORIES = [
  { key: "users", label: "Users", description: "Manage user accounts and profiles" },
  { key: "roles", label: "Roles", description: "Manage roles and permissions" },
  { key: "projects", label: "Projects", description: "Manage projects and assignments" },
  { key: "estimates", label: "Estimates", description: "Create and manage estimates" },
  { key: "invoices", label: "Invoices", description: "Handle billing and invoices" },
  { key: "shifts", label: "Shifts", description: "Manage work shifts and scheduling" },
  { key: "shift_users", label: "Shift Users", description: "Manage shift user assignments" },
  { key: "shift_positions", label: "Shift Positions", description: "Manage shift positions" },
  { key: "time_cards", label: "Time Cards", description: "Manage employee time tracking" },
  { key: "clients", label: "Clients", description: "Manage client accounts and information" },
  { key: "contacts", label: "Contacts", description: "Manage contact information" },
  { key: "expenses", label: "Expenses", description: "Track and manage expenses" },
  { key: "incidents", label: "Incidents", description: "Manage safety incidents and reports" },
  { key: "investigations", label: "Investigations", description: "Handle incident investigations" },
];

const SPECIAL_PERMISSIONS = [
  { key: "tenant", label: "Tenant Settings", description: "Update tenant configuration", actions: ["update"] },
];

export default function RoleCreatePage() {
  const { colors } = useTheme();
  const { userRoles, tenantId, session } = useAuth();
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"basic" | "users" | "permissions" | "phones">("basic");
  const [twilioConfigured, setTwilioConfigured] = useState<boolean | null>(null);

  const [roleData, setRoleData] = useState<RoleFormData>({
    name: "",
    description: "",
    meta_data: {
      users: [],
      permissions: {},
    },
  });

  const [users, setUsers] = useState<any[]>([]);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [showPhonePicker, setShowPhonePicker] = useState(false);

  const permissions = getEntityPermissions(userRoles, "roles");

  // Check Twilio configuration
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.functions.invoke("twilio-api-status", {
          body: {},
          headers: { Authorization: `Bearer ${session?.access_token}` },
        });
        setTwilioConfigured(Boolean(data?.hasTwilio ?? data?.accountSid));
      } catch {
        setTwilioConfigured(false);
      }
    })();
  }, [session?.access_token]);

  // Check permissions
  if (!permissions.create) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.centered}>
          <MaterialCommunityIcons name="lock" size={48} color={colors.textSecondary} />
          <ThemedText type="defaultSemiBold" style={{ marginTop: 16 }}>
            Access Denied
          </ThemedText>
          <ThemedText style={{ opacity: 0.7, marginTop: 8 }}>You don&apos;t have permission to create roles</ThemedText>
        </View>
      </View>
    );
  }

  const updateRoleData = (updates: Partial<RoleFormData>) => {
    setRoleData((prev) => ({ ...prev, ...updates }));
  };

  const updateMetaData = (updates: Partial<RoleFormData["meta_data"]>) => {
    setRoleData((prev) => ({
      ...prev,
      meta_data: { ...prev.meta_data, ...updates },
    }));
  };

  const handlePermissionToggle = (category: string, action: string, value: boolean) => {
    const currentPermissions = roleData.meta_data?.permissions || {};

    if (action === "boolean") {
      // Handle boolean permissions like admin_portal and integrations
      updateMetaData({
        permissions: {
          ...currentPermissions,
          [category]: value,
        },
      });
    } else {
      // Handle CRUD permissions
      const categoryPerms = currentPermissions[category] || {};
      updateMetaData({
        permissions: {
          ...currentPermissions,
          [category]: {
            ...categoryPerms,
            [action]: value,
          },
        },
      });
    }
  };

  const handleUserSelection = (selectedUsers: any[]) => {
    updateMetaData({
      users: selectedUsers.map((u) => u.id!),
    });
    setUsers(selectedUsers);
    setShowUserPicker(false);
  };

  const handleSave = async () => {
    if (!roleData.name.trim()) {
      toast.showToast({
        title: "Missing Role Name",
        message: "Please enter a name for this role",
        toastType: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      // Extract users from meta_data - they go in the link table, not meta_data
      const { users: userIds, ...metaDataWithoutUsers } = roleData.meta_data || {};

      const newRole = await roleService.addRole({
        name: roleData.name,
        description: roleData.description,
        meta_data: metaDataWithoutUsers,
      });

      const roleId = newRole?.id as string;
      if (roleId && tenantId) {
        // Sync users to link table if any were selected
        if (userIds && userIds.length > 0) {
          await roleService.syncRoleUsers(roleId, tenantId, userIds);
        }

        toast.showToast({
          title: "Role created",
          message: "Role has been successfully created",
          toastType: "success",
        });

        // Navigate to the new role's edit page
        router.replace(`/settings/roles/${roleId}`);
      }
    } catch (error: any) {
      toast.showToast({
        title: "Creation failed",
        message: error?.message || "Failed to create role",
        toastType: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    router.push("/(app)/settings/roles");
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "basic":
        return (
          <View style={styles.tabContent}>
            <View style={styles.section}>
              <ThemedText style={styles.sectionTitle}>Basic Information</ThemedText>
              <ThemedInput
                label="Role Name *"
                value={roleData.name}
                onChangeText={(text) => updateRoleData({ name: text })}
                placeholder="Enter role name"
                style={styles.input}
              />
              <ThemedInput
                label="Description"
                value={roleData.description}
                onChangeText={(text) => updateRoleData({ description: text })}
                placeholder="Enter role description"
                multiline
                numberOfLines={3}
                style={styles.textArea}
              />
              <View style={styles.toggleRow}>
                <View style={styles.toggleInfo}>
                  <ThemedText style={styles.inputLabel}>Enroll Users For Shifts</ThemedText>
                  <ThemedText style={styles.toggleDescription}>
                    Users with this role will be available for shift assignments and scheduling
                  </ThemedText>
                </View>
                <TouchableOpacity
                  style={[
                    styles.toggleSwitch,
                    {
                      backgroundColor: roleData.meta_data?.accept_shifts ? colors.primary : colors.border,
                    },
                  ]}
                  onPress={() => {
                    const newAcceptShifts = !roleData.meta_data?.accept_shifts;
                    updateMetaData({
                      accept_shifts: newAcceptShifts,
                      // Set priority to 1 when enabling accept_shifts if not already set
                      ...(newAcceptShifts && !roleData.meta_data?.priority ? { priority: 1 } : {}),
                    });
                  }}
                >
                  <View
                    style={[
                      styles.toggleThumb,
                      {
                        backgroundColor: colors.card,
                        transform: [{ translateX: roleData.meta_data?.accept_shifts ? 20 : 2 }],
                      },
                    ]}
                  />
                </TouchableOpacity>
              </View>
              {roleData.meta_data?.accept_shifts && (
                <ThemedInput
                  label="Priority (1-10)"
                  value={String(roleData.meta_data?.priority ?? 1)}
                  onChangeText={(text) => {
                    const num = parseInt(text, 10);
                    const priority = isNaN(num) ? 1 : Math.max(1, Math.min(10, num));
                    updateMetaData({ priority });
                  }}
                  keyboardType="numeric"
                  placeholder="1"
                  style={styles.input}
                />
              )}
            </View>
          </View>
        );

      case "users":
        return (
          <View style={styles.tabContent}>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <ThemedText style={styles.sectionTitle}>
                  User Assignment ({roleData.meta_data?.users?.length || 0})
                </ThemedText>
                <Button
                  text="Select Users"
                  icon="account-plus"
                  onPress={() => setShowUserPicker(true)}
                  style={styles.actionButton}
                />
              </View>

              {users.length > 0 ? (
                <View style={styles.userList}>
                  {users.map((user) => {
                    const displayName = user.meta_data?.preferred_name || user.first_name;
                    const fullName = `${displayName} ${user.last_name}`.trim();

                    return (
                      <View key={user.id} style={[styles.userItem, { borderBottomColor: colors.border }]}>
                        <View style={styles.userInfo}>
                          <ThemedText type="defaultSemiBold">{fullName || "Unknown User"}</ThemedText>
                          {user.email_address && (
                            <ThemedText style={styles.userDetail}>{user.email_address}</ThemedText>
                          )}
                          {user.meta_data?.roles && user.meta_data.roles.length > 0 && (
                            <ThemedText style={styles.userRole}>Roles: {user.meta_data.roles.join(", ")}</ThemedText>
                          )}
                        </View>
                        <TouchableOpacity
                          onPress={() => {
                            const updatedUsers = users.filter((u) => u.id !== user.id);
                            setUsers(updatedUsers);
                            updateMetaData({
                              users: updatedUsers.map((u) => u.id!),
                            });
                          }}
                          style={styles.removeButton}
                        >
                          <MaterialCommunityIcons name="close-circle" size={24} color={colors.dangerText} />
                        </TouchableOpacity>
                      </View>
                    );
                  })}
                </View>
              ) : (
                <View style={styles.emptyState}>
                  <MaterialCommunityIcons name="account-group-outline" size={48} color={colors.textSecondary} />
                  <ThemedText style={styles.emptyText}>No users assigned</ThemedText>
                  <ThemedText style={styles.emptySubtext}>
                    Users assigned to this role will inherit its permissions
                  </ThemedText>
                </View>
              )}
            </View>
          </View>
        );

      case "permissions":
        return (
          <View style={styles.tabContent}>
            <View style={styles.section}>
              <ThemedText style={styles.sectionTitle}>Basic Permissions</ThemedText>
              <ThemedText style={styles.sectionSubtitle}>
                Configure initial permissions. You can refine these later in the role editor.
              </ThemedText>

              {/* Boolean Permissions */}
              <ThemedText style={styles.permissionSectionTitle}>System Access</ThemedText>
              {BOOLEAN_PERMISSIONS.map((permission) => {
                const isEnabled = roleData.meta_data?.permissions?.[permission.key] === true;

                return (
                  <View key={permission.key} style={styles.permissionCategory}>
                    <View style={styles.permissionHeader}>
                      <View style={styles.permissionInfo}>
                        <ThemedText style={styles.permissionTitle}>{permission.label}</ThemedText>
                        <ThemedText style={styles.permissionDescription}>{permission.description}</ThemedText>
                      </View>
                      <TouchableOpacity
                        style={[
                          styles.toggleSwitch,
                          {
                            backgroundColor: isEnabled ? colors.primary : colors.border,
                          },
                        ]}
                        onPress={() => handlePermissionToggle(permission.key, "boolean", !isEnabled)}
                      >
                        <View
                          style={[
                            styles.toggleThumb,
                            {
                              backgroundColor: colors.card,
                              transform: [{ translateX: isEnabled ? 20 : 2 }],
                            },
                          ]}
                        />
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              })}

              {/* CRUD Permissions */}
              <ThemedText style={[styles.permissionSectionTitle, { marginTop: 16 }]}>Entity Management</ThemedText>
              {CRUD_PERMISSION_CATEGORIES.map((category) => {
                const categoryPerms = roleData.meta_data?.permissions?.[category.key] || {};

                return (
                  <View key={category.key} style={styles.permissionCategory}>
                    <View style={styles.permissionHeader}>
                      <View style={styles.permissionInfo}>
                        <ThemedText style={styles.permissionTitle}>{category.label}</ThemedText>
                      </View>
                      <View style={styles.permissionActions}>
                        {["read", "create", "update", "delete"].map((action) => (
                          <TouchableOpacity
                            key={action}
                            style={[
                              styles.permissionChip,
                              {
                                backgroundColor: categoryPerms[action]
                                  ? colors.primary + "20"
                                  : colors.backgroundSecondary,
                                borderColor: categoryPerms[action] ? colors.primary : colors.border,
                              },
                            ]}
                            onPress={() => handlePermissionToggle(category.key, action, !categoryPerms[action])}
                          >
                            <ThemedText
                              style={[
                                styles.permissionChipText,
                                { color: categoryPerms[action] ? colors.primary : colors.text },
                              ]}
                            >
                              {action}
                            </ThemedText>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>
                  </View>
                );
              })}

              {/* Special Permissions */}
              <ThemedText style={[styles.permissionSectionTitle, { marginTop: 16 }]}>Special Permissions</ThemedText>
              {SPECIAL_PERMISSIONS.map((permission) => {
                const categoryPerms = roleData.meta_data?.permissions?.[permission.key] || {};

                return (
                  <View key={permission.key} style={styles.permissionCategory}>
                    <View style={styles.permissionHeader}>
                      <View style={styles.permissionInfo}>
                        <ThemedText style={styles.permissionTitle}>{permission.label}</ThemedText>
                      </View>
                      <View style={styles.permissionActions}>
                        {permission.actions.map((action) => (
                          <TouchableOpacity
                            key={action}
                            style={[
                              styles.permissionChip,
                              {
                                backgroundColor: categoryPerms[action]
                                  ? colors.primary + "20"
                                  : colors.backgroundSecondary,
                                borderColor: categoryPerms[action] ? colors.primary : colors.border,
                              },
                            ]}
                            onPress={() => handlePermissionToggle(permission.key, action, !categoryPerms[action])}
                          >
                            <ThemedText
                              style={[
                                styles.permissionChipText,
                                { color: categoryPerms[action] ? colors.primary : colors.text },
                              ]}
                            >
                              {action}
                            </ThemedText>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        );

      case "phones":
        return (
          <View style={styles.tabContent}>
            <View style={styles.section}>
              {twilioConfigured === null ? (
                <ThemedText style={{ opacity: 0.5, textAlign: "center", paddingVertical: 32 }}>
                  Checking phone configuration...
                </ThemedText>
              ) : !twilioConfigured ? (
                <View style={styles.notConfiguredContainer}>
                  <MaterialCommunityIcons name="phone-off" size={48} color={colors.textSecondary} />
                  <ThemedText type="defaultSemiBold" style={{ marginTop: 16 }}>
                    Twilio Not Configured
                  </ThemedText>
                  <ThemedText style={{ opacity: 0.6, textAlign: "center", marginTop: 8 }}>
                    Phone assignment requires Twilio integration to be configured.
                  </ThemedText>
                  <ThemedText style={{ opacity: 0.4, textAlign: "center", marginTop: 4, fontSize: 12 }}>
                    Go to Settings → Integrations to configure Twilio.
                  </ThemedText>
                </View>
              ) : (
                <>
                  <View style={styles.sectionHeader}>
                    <ThemedText style={styles.sectionTitle}>
                      Phone Assignment ({roleData.meta_data?.permissions?.phones?.length || 0})
                    </ThemedText>
                    <Button
                      text="Select Phones"
                      icon="phone-plus"
                      onPress={() => setShowPhonePicker(true)}
                      style={styles.actionButton}
                    />
                  </View>

                  {roleData.meta_data?.permissions?.phones && roleData.meta_data.permissions.phones.length > 0 ? (
                    <View style={styles.phonesList}>
                      {roleData.meta_data.permissions.phones.map((phone: any, index: number) => (
                        <View
                          key={phone.number}
                          style={[styles.phoneCard, { borderColor: colors.border, backgroundColor: colors.card }]}
                        >
                          <View style={styles.phoneRow}>
                            <View style={{ flex: 1 }}>
                              <ThemedText style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
                                Phone Number
                              </ThemedText>
                              <ThemedText type="defaultSemiBold">{phone.number}</ThemedText>
                            </View>
                            <TouchableOpacity
                              onPress={() => {
                                const phones = roleData.meta_data?.permissions?.phones || [];
                                const updatedPhones = phones.filter((_: any, i: number) => i !== index);
                                updateMetaData({
                                  permissions: {
                                    ...roleData.meta_data?.permissions,
                                    phones: updatedPhones,
                                  },
                                });
                              }}
                              style={styles.removeButton}
                            >
                              <MaterialCommunityIcons name="close-circle" size={24} color={colors.dangerText} />
                            </TouchableOpacity>
                          </View>
                          <ThemedInput
                            label="Alias (optional)"
                            value={phone.alias || ""}
                            onChangeText={(v) => {
                              const phones = roleData.meta_data?.permissions?.phones || [];
                              const updatedPhones = [...phones];
                              updatedPhones[index] = { ...updatedPhones[index], alias: v };
                              updateMetaData({
                                permissions: {
                                  ...roleData.meta_data?.permissions,
                                  phones: updatedPhones,
                                },
                              });
                            }}
                            placeholder="e.g., Main Office Line"
                            style={{ marginBottom: 8 }}
                          />
                          <View style={styles.phoneRow}>
                            <ThemedText style={{ flex: 1, opacity: 0.7 }}>
                              Hide Number {!phone.alias ? "(requires alias)" : ""}
                            </ThemedText>
                            <TouchableOpacity
                              style={[
                                styles.toggleSwitch,
                                {
                                  backgroundColor: !phone.show_number ? colors.primary : colors.border,
                                  opacity: !phone.alias ? 0.5 : 1,
                                },
                              ]}
                              onPress={() => {
                                if (!phone.alias) return;
                                const phones = roleData.meta_data?.permissions?.phones || [];
                                const updatedPhones = [...phones];
                                updatedPhones[index] = { ...updatedPhones[index], show_number: !phone.show_number };
                                updateMetaData({
                                  permissions: {
                                    ...roleData.meta_data?.permissions,
                                    phones: updatedPhones,
                                  },
                                });
                              }}
                              disabled={!phone.alias}
                            >
                              <View
                                style={[
                                  styles.toggleThumb,
                                  {
                                    backgroundColor: colors.card,
                                    transform: [{ translateX: phone.show_number ? 20 : 2 }],
                                  },
                                ]}
                              />
                            </TouchableOpacity>
                          </View>
                        </View>
                      ))}
                    </View>
                  ) : (
                    <View style={styles.emptyState}>
                      <MaterialCommunityIcons name="phone-outline" size={48} color={colors.textSecondary} />
                      <ThemedText style={styles.emptyText}>No phones assigned</ThemedText>
                      <ThemedText style={styles.emptySubtext}>
                        Assign phones to allow users with this role to use them
                      </ThemedText>
                    </View>
                  )}
                </>
              )}
            </View>
          </View>
        );

      default:
        return null;
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundSecondary }]}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <View style={styles.headerLeft}>
          <TouchableOpacity onPress={handleCancel} style={styles.backButton}>
            <MaterialCommunityIcons name="arrow-left" size={24} color={colors.text} />
          </TouchableOpacity>
          <View>
            <ThemedText type="title">Create Role</ThemedText>
            <ThemedText style={styles.subtitle}>Configure a new role with permissions</ThemedText>
          </View>
        </View>
        <View style={styles.headerActions}>
          <Button text="Cancel" onPress={handleCancel} style={styles.cancelButton} />
          <Button
            text={loading ? "Creating..." : "Create Role"}
            onPress={handleSave}
            disabled={loading || !roleData.name.trim()}
            style={styles.saveButton}
          />
        </View>
      </View>

      {/* Tabs */}
      <View style={[styles.tabBar, { borderBottomColor: colors.border }]}>
        {[
          { key: "basic", label: "Basic Info", icon: "information-outline" },
          { key: "users", label: "Users", icon: "account-group" },
          { key: "permissions", label: "Permissions", icon: "shield-check" },
          { key: "phones", label: "Phones", icon: "phone" },
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              {
                borderBottomColor: activeTab === tab.key ? colors.primary : "transparent",
              },
            ]}
            onPress={() => setActiveTab(tab.key as any)}
          >
            <MaterialCommunityIcons
              name={tab.icon as any}
              size={20}
              color={activeTab === tab.key ? colors.primary : colors.textSecondary}
            />
            <ThemedText
              style={[styles.tabText, { color: activeTab === tab.key ? colors.primary : colors.textSecondary }]}
            >
              {tab.label}
            </ThemedText>
          </TouchableOpacity>
        ))}
      </View>

      {/* Content */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {renderTabContent()}
      </ScrollView>

      {/* User Picker Modal */}
      {showUserPicker && (
        <View style={[styles.modalOverlay, { backgroundColor: colors.background + "CC" }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
            <UserPicker
              multiple
              selectedUserIds={roleData.meta_data?.users || []}
              onMultiPick={handleUserSelection}
              onCancel={() => setShowUserPicker(false)}
            />
          </View>
        </View>
      )}

      {/* Phone Picker Modal */}
      {showPhonePicker && (
        <View style={[styles.modalOverlay, { backgroundColor: colors.background + "CC" }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.card }]}>
            <PhonePicker
              pickMany
              selectedNumbers={roleData.meta_data?.permissions?.phones?.map((p: any) => p.number) || []}
              onPick={(selectedNumbers) => {
                const currentPhones = roleData.meta_data?.permissions?.phones || [];
                const newPhones = selectedNumbers.map((number) => {
                  const existingPhone = currentPhones.find((p: any) => p.number === number);
                  if (existingPhone) {
                    return existingPhone;
                  }
                  return {
                    number,
                    alias: "",
                    show_number: true,
                  };
                });
                updateMetaData({
                  permissions: {
                    ...roleData.meta_data?.permissions,
                    phones: newPhones,
                  },
                });
                setShowPhonePicker(false);
              }}
              onCancel={() => setShowPhonePicker(false)}
            />
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  backButton: {
    marginRight: 12,
    padding: 4,
  },
  subtitle: {
    opacity: 0.7,
    marginTop: 2,
  },
  headerActions: {
    flexDirection: "row",
    gap: 8,
  },
  cancelButton: {
    paddingHorizontal: 16,
  },
  saveButton: {
    paddingHorizontal: 16,
  },
  tabBar: {
    flexDirection: "row",
    borderBottomWidth: 1,
  },
  tab: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 8,
    gap: 6,
    borderBottomWidth: 2,
  },
  tabText: {
    fontSize: 14,
    fontWeight: "500",
  },
  content: {
    flex: 1,
  },
  tabContent: {
    padding: 16,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: 14,
    opacity: 0.7,
    marginBottom: 16,
  },
  actionButton: {
    paddingHorizontal: 12,
  },
  input: {
    marginBottom: 12,
  },
  textArea: {
    marginBottom: 12,
    minHeight: 80,
  },
  chipContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  userList: {
    gap: 0,
  },
  userItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
  },
  userInfo: {
    flex: 1,
    gap: 2,
  },
  userDetail: {
    fontSize: 12,
    opacity: 0.6,
  },
  userRole: {
    fontSize: 11,
    opacity: 0.5,
    fontStyle: "italic",
  },
  removeButton: {
    padding: 4,
  },
  moreText: {
    fontSize: 12,
    opacity: 0.6,
    alignSelf: "center",
  },
  emptyState: {
    alignItems: "center",
    padding: 32,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "500",
    marginTop: 12,
  },
  emptySubtext: {
    fontSize: 14,
    opacity: 0.7,
    textAlign: "center",
    marginTop: 4,
  },
  permissionCategory: {
    marginBottom: 8,
    paddingBottom: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#00000010",
  },
  permissionSectionTitle: {
    fontSize: 16,
    fontWeight: "700",
    marginBottom: 8,
    opacity: 0.8,
  },
  permissionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  permissionInfo: {
    flex: 1,
    marginRight: 12,
  },
  permissionTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  permissionDescription: {
    fontSize: 14,
    opacity: 0.7,
    marginTop: 2,
  },
  toggleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
  },
  toggleInfo: {
    flex: 1,
    marginRight: 12,
  },
  toggleDescription: {
    fontSize: 12,
    opacity: 0.6,
    marginTop: 2,
  },
  inputLabel: {
    fontSize: 14,
    opacity: 0.7,
    fontWeight: "500",
  },
  toggleSwitch: {
    width: 44,
    height: 24,
    borderRadius: 12,
    justifyContent: "center",
    padding: 2,
  },
  toggleThumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    boxShadow: "0 2px 2.22px 0 rgba(0, 0, 0, 0.22)",
    elevation: 3,
  },
  permissionActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  permissionChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
  },
  permissionChipText: {
    fontSize: 12,
    fontWeight: "500",
    textTransform: "capitalize",
  },
  modalOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    width: "90%",
    maxWidth: 500,
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.25)",
    elevation: 5,
  },
  notConfiguredContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 48,
    gap: 4,
  },
  phonesList: {
    gap: 12,
  },
  phoneCard: {
    padding: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 8,
    gap: 8,
  },
  phoneRow: {
    flexDirection: "row",
    gap: 12,
    alignItems: "center",
  },
});
